import { useId } from 'react';

import {
  Area,
  AreaChart,
  PolarAngleAxis,
  RadialBar,
  RadialBarChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';

import type { PerformanceConfig } from '@pages/PracticePage/model';

import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  Heading,
  type ChartConfig,
} from '@shared/ui';

interface PerformanceOverviewProps {
  performance: PerformanceConfig;
}

export const PerformanceOverview = ({ performance }: PerformanceOverviewProps) => {
  const gradientId = useId();
  const accuracy = Math.max(0, Math.min(performance.accuracy, 100));
  const progressData = performance.progress.map((value, index) => ({
    day: index + 1,
    progress: value,
  }));
  const lastProgressValue = progressData[progressData.length - 1]?.progress ?? 0;
  const progressMin = progressData.reduce(
    (minValue, item) => Math.min(minValue, item.progress),
    Number.POSITIVE_INFINITY
  );
  const progressLabelMin = Number.isFinite(progressMin) ? Math.round(progressMin) : 0;
  const accuracyChartConfig: ChartConfig = {
    accuracy: {
      label: 'Accuracy',
      color: performance.accuracyColor,
    },
  };
  const progressChartConfig: ChartConfig = {
    progress: {
      label: 'Progress',
      color: performance.progressColor,
    },
  };

  return (
    <section>
      <Heading tag="h2" size="base" aboveCard>
        Performance overview
      </Heading>
      <div
        className="
        mt-3 flex flex-col gap-6 rounded-2xl border border-border/60 bg-card p-4
        shadow-sm
      "
      >
        <div className="">
          <div className="text-sm font-medium text-muted-foreground">Accuracy</div>
          <ChartContainer config={accuracyChartConfig} className="h-28">
            <ResponsiveContainer width="100%" height="100%">
              <RadialBarChart
                data={[{ name: 'accuracy', value: accuracy }]}
                startAngle={180}
                endAngle={0}
                innerRadius="170%"
                outerRadius="200%"
                cy="90%"
              >
                <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                <RadialBar
                  dataKey="value"
                  cornerRadius={999}
                  background={{ fill: '#ebe7e0' }}
                  fill="var(--color-accuracy)"
                  isAnimationActive={true}
                />
              </RadialBarChart>
            </ResponsiveContainer>
            <div
              className="
              pointer-events-none absolute inset-0 flex flex-col items-center
              justify-end pb-1
            "
            >
              <div className="text-2xl font-medium text-foreground">{accuracy}%</div>
              <div
                className="
                text-[10px] font-medium tracking-wide text-muted-foreground
                uppercase
              "
              >
                Correct
              </div>
            </div>
          </ChartContainer>
        </div>

        <div className="border-t pt-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-muted-foreground">Progress (30d)</span>
            <span className="text-xs font-medium text-foreground">{lastProgressValue}%</span>
          </div>
          <ChartContainer config={progressChartConfig} className="mt-4 h-32">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={progressData} margin={{ top: 8, left: 0, right: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--color-progress)" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="var(--color-progress)" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="day" hide />
                <YAxis
                  hide
                  domain={[
                    (dataMin: number) => (typeof dataMin === 'number' ? dataMin - 4 : 0),
                    (dataMax: number) => (typeof dataMax === 'number' ? dataMax + 4 : 100),
                  ]}
                />
                <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel />} />
                <Area
                  type="monotone"
                  dataKey="progress"
                  stroke="var(--color-progress)"
                  strokeWidth={2}
                  dot={false}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill={`url(#${gradientId})`}
                  isAnimationActive={true}
                />
              </AreaChart>
            </ResponsiveContainer>
            <div
              className="
              pointer-events-none absolute inset-0 flex items-end justify-end
              pr-1 pb-1
            "
            >
              <span className="text-[11px] font-medium text-muted-foreground">
                {progressLabelMin}%
              </span>
            </div>
          </ChartContainer>
        </div>
      </div>
    </section>
  );
};
